/*
 * dashcam/index.js — /dashcam: the phone on the windshield as a dash camera.
 *
 * Pieces (each its own module, the arithmetic of each under a unit test):
 *   ring.js     which encoded frames the loop keeps, where each one lies on
 *               disk, and the window of an event;
 *   store.js    the bytes — a worker writing into the private file system
 *               with sync access handles, so a dead tab loses a second, not the
 *               drive; memory where the browser cannot do that;
 *   capture.js  camera → canvas with the stamp → H.264, audio through the AAC door;
 *   mux.js      a stretch of the loop → one MP4, without re-encoding;
 *   gsensor.js  a crash told apart from a pothole by direction;
 *   overlay.js  date, time, speed, coordinates, plate — burned into the frames.
 * GPS speed and the trip are the /gps-speedometer core (GpsSpeedCore), the GPX
 * writer is the /gpx-viewer one (GpsWrite): one arithmetic of a drive per site.
 *
 * What the browser does not allow is said up front, on the page and in the
 * FAQ: a web page cannot record with the screen locked or behind another app —
 * the browser takes the camera away. So the recorder stops itself the moment
 * the page is hidden, keeps what it had, finishes a started event, and says so
 * when the person comes back.
 */

import { RingIndex, headersFromWalk, openEvent, extendEvent, eventReady, loopBytes, fitLoop, isVideo } from './ring.js';
import { LoopWriter } from './loop-writer.js';
import { ImpactDetector, sampleFromEvent } from './gsensor.js';
import { makeClock, stampParts } from './overlay.js';
import { openStore, MemoryStore } from './store.js';
import { buildMp4, estimateBytes } from './mux.js';
import { Capture, outputSize, bitrateFor } from './capture.js';

const CFG = window.__DASHCAM_CFG || {};
const T = CFG.lang || {};
const SLUG = 'dashcam';
const $ = (id) => document.getElementById(id);
const MB = 1048576;
/* A fix older than this says nothing about the speed NOW (a tunnel, a lost
   signal): the stamp shows a dash instead of a stale number. Phones deliver a
   fix every 1–2 s while moving; a parked phone may go quieter. */
const GPS_FRESH_MS = 10000;

function tr(k, vars) {
    let s = T[k] != null ? String(T[k]) : k;
    if (vars) for (const v of Object.keys(vars).sort((a, b) => b.length - a.length)) s = s.split(':' + v).join(String(vars[v]));
    return s;
}
function ae(f, p) { try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, f }, p || {})); } catch (_) {} }
function stage(n, m) { try { if (window.__stage) window.__stage(n, m || {}); } catch (_) {} }
function diag(issue, p) { try { if (window.__aeDiagnostic) window.__aeDiagnostic(Object.assign({ issue }, p || {})); } catch (_) {} }
function complete(p) { try { if (window.__aeToolComplete) window.__aeToolComplete(p || {}); } catch (_) {} }
function toast(m, t) { try { if (window.__toast) window.__toast(m, t || 'info'); } catch (_) {} }
function announce(m) { try { if (window.__announce) window.__announce(m); } catch (_) {} }
function errName(e) { return String((e && (e.name || e.code)) || 'Error').slice(0, 40); }

const TS = window.__toolSettings || { load: (_s, _k, d) => d, save: () => {} };

/* ─────────────────────────── settings ─────────────────────────── */

/* Miles only where the road signs are in miles and the page is English. */
function defaultUnit() {
    const page = String(CFG.locale || document.documentElement.lang || 'en').toLowerCase();
    const l = String((navigator.languages && navigator.languages[0]) || navigator.language || '');
    return page === 'en' && /^en-(US|GB|LR)$/i.test(l) ? 'mph' : 'kmh';
}
const DEF = {
    res: 720, loop: 5, pre: 60, post: 30, audio: false,
    gthr: 1.5, ghoriz: true,
    ovTime: true, ovSpeed: true, ovCoords: true, ovText: false, text: '',
    unit: defaultUnit(), autoDim: false, fullscreen: true,
};
const set = {};
for (const k of Object.keys(DEF)) {
    let v = TS.load(SLUG, k, DEF[k]);
    if (typeof DEF[k] === 'number') v = Number(v); else if (typeof DEF[k] === 'boolean') v = !!v; else v = v == null ? DEF[k] : String(v);
    if (typeof DEF[k] === 'number' && !isFinite(v)) v = DEF[k];
    set[k] = v;
}
const RES_WH = { 480: [854, 480], 720: [1280, 720], 1080: [1920, 1080] };
if (!RES_WH[set.res]) set.res = 720;
if ([1, 3, 5, 10].indexOf(set.loop) < 0) set.loop = 5;
if ([10, 30, 60].indexOf(set.pre) < 0) set.pre = 60;
if ([10, 30, 60].indexOf(set.post) < 0) set.post = 30;
if (!(set.gthr >= 0.5 && set.gthr <= 3)) set.gthr = 1.5;
function save(k, v) { set[k] = v; TS.save(SLUG, k, v); }

/* ─────────────────────────── state ─────────────────────────── */

const Core = window.GpsSpeedCore;
const S = {
    state: 'idle',            // idle | preview | recording | stopping
    busy: false,
    cam: null, stream: null, mic: null, micStream: null,
    store: null, storeP: null,
    sess: null, ix: null,
    batch: [], batchBytes: 0, batchSeg: null,
    pending: [], building: new Set(), buildChain: Promise.resolve(),
    events: [], prev: [],
    gps: null, prevFix: null, gpsWatch: null, gpsDenied: false,
    smoother: Core ? new Core.SpeedSmoother() : null,
    trip: Core ? new Core.TripAccumulator() : null,
    track: Core ? new Core.TrackRecorder(20000) : null,
    tripStart: null,
    gs: new ImpactDetector({ thresholdG: set.gthr, horizontalOnly: set.ghoriz }),
    gNow: 0, motionOn: false, motionSeen: false, motionAsked: false, motionPerm: null,
    lastImpact: null,
    timers: {},
    dimmed: false, lastTouch: 0,
    batt: null, battWarned: false,
    lastLoopFile: null,
    recStartedAt: 0,
    stopReason: null,
};

/* The date order of the reader's own region when it speaks the page's language
   (en-GB writes 26/09, en-US 09/26), else the page's. */
const pageLoc = CFG.locale || document.documentElement.lang || 'en';
const navLoc = String(navigator.language || '');
const clock = makeClock(navLoc.toLowerCase().split('-')[0] === pageLoc.toLowerCase() ? navLoc : pageLoc);
const unitLabels = { kmh: T.u_kmh || 'km/h', mph: T.u_mph || 'mph' };

const el = {
    app: $('dcApp'), stage: $('dcStage'), screen: $('dcScreen'), video: $('dcVideo'), canvas: $('dcCanvas'),
    idle: $('dcIdle'), hud: $('dcHud'), recTime: $('dcRecTime'), loopInfo: $('dcLoopInfo'), batt: $('dcBatt'),
    evBanner: $('dcEvBanner'), evLeft: $('dcEvLeft'), notice: $('dcNotice'), camNote: $('dcCamNote'),
    start: $('dcStart'), preview: $('dcPreview'), save: $('dcSave'), stop: $('dcStop'), dimBtn: $('dcDimBtn'),
    fs: $('dcFs'), min: $('dcMin'), dim: $('dcDim'), dimTime: $('dcDimTime'),
    speed: $('dcSpeedBig'), speedUnit: $('dcSpeedUnit'), gNow: $('dcGNow'), gpsState: $('dcGpsState'),
    idleCtl: $('dcIdleCtl'), liveCtl: $('dcLiveCtl'),
    unsupported: $('dcUnsupported'),
    loopCard: $('dcLoopCard'), loopStatus: $('dcLoopStatus'), saveLoop: $('dcSaveLoop'),
    evList: $('dcEvList'), evEmpty: $('dcEvEmpty'), evTotal: $('dcEvTotal'), evAll: $('dcEvAll'),
    trip: { dist: $('dcTripDist'), time: $('dcTripTime'), max: $('dcTripMax'), avg: $('dcTripAvg') },
    gpx: $('dcGpx'), tripReset: $('dcTripReset'), tripNote: $('dcTripNote'),
    prevCard: $('dcPrevCard'), prevList: $('dcPrevList'),
    storage: $('dcStorage'),
    gMeter: $('dcGMeter'), gBar: $('dcGBar'), gMark: $('dcGMark'), gVal: $('dcGVal'), gPeak: $('dcGPeak'),
    gState: $('dcGState'), gPerm: $('dcGPerm'), gThr: $('dcGThr'), gThrVal: $('dcGThrVal'),
    textIn: $('dcText'),
};

/* ─────────────────────────── helpers ─────────────────────────── */

function fmtDur(ms) {
    const s = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
    const p = (n) => (n < 10 ? '0' : '') + n;
    return h ? h + ':' + p(m) + ':' + p(ss) : m + ':' + p(ss);
}
function fmtBytes(n) {
    if (!(n > 0)) return '0 ' + (T.u_mb || 'MB');
    if (n >= 1024 * MB) return (n / 1024 / MB).toFixed(n >= 10 * 1024 * MB ? 0 : 1) + ' ' + (T.u_gb || 'GB');
    return Math.max(1, Math.round(n / MB)) + ' ' + (T.u_mb || 'MB');
}
function fmtClock(ms) { return clock(ms); }
function speedIn(mps) {
    if (mps == null || !isFinite(mps)) return '—';
    const v = mps * (set.unit === 'mph' ? 2.2369362920544 : 3.6);
    return String(Math.round(v));
}
function b64(u8) { if (!u8) return null; let s = ''; for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]); return btoa(s); }
function unb64(s) { if (!s) return null; const b = atob(s); const u = new Uint8Array(b.length); for (let i = 0; i < b.length; i++) u[i] = b.charCodeAt(i); return u; }
function isTouch() { try { return window.matchMedia('(pointer: coarse)').matches; } catch (_) { return false; } }
function show(node, on) { if (node) node.hidden = !on; }
function nowUs() { return S.sess ? Math.round((performance.now() - S.sess.t0) * 1000) : 0; }
function visibleTimeout(fn, ms) {
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    const id = setTimeout(fn, ms); return () => clearTimeout(id);
}

function supportsRecording() {
    return typeof VideoEncoder === 'function' && typeof VideoFrame === 'function' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

/* ─────────────────────────── capture ─────────────────────────── */

const capture = new Capture({
    video: el.video,
    canvas: el.canvas,
    stamp: () => {
        const g = S.gps, fresh = g && Date.now() - g.wall < GPS_FRESH_MS;
        return { now: Date.now(), mps: fresh ? g.mps : null, unit: set.unit, lat: g ? g.lat : null, lon: g ? g.lon : null, text: set.text };
    },
    stampOn: () => ({ time: set.ovTime, speed: set.ovSpeed, coords: set.ovCoords, text: set.ovText && !!set.text }),
    stampParts: (st, on) => stampParts(st, on, clock, unitLabels),
    onRecord: (rec) => onRecord(rec),
    onConfig: (kind, cfg) => onConfig(kind, cfg),
    onError: (e) => onCaptureError(e),
    onStats: (st) => { S.lastStats = st; },
    telemetry: (f, p) => ae(f, p),
});

/* ─────────────────────────── camera ─────────────────────────── */

function camNote(msg) {
    if (!el.camNote) return;
    el.camNote.textContent = msg || '';
    show(el.camNote, !!msg);
}

async function openCamera(btn) {
    const live = S.stream && S.stream.getVideoTracks().some(t => t.readyState === 'live');
    if (live && S.streamRes === set.res) return S.stream;
    camNote('');
    if (typeof window.CameraManager !== 'function') { camNote(T.cam_module_missing); throw new Error('camera module missing'); }
    S.cam = S.cam || new window.CameraManager({ slug: SLUG });
    const wh = RES_WH[set.res];
    let stream;
    try {
        stream = await S.cam.start({ facingMode: 'environment', width: wh[0], height: wh[1], button: btn || undefined });
    } catch (e) {
        const code = (e && e.code) || (window.CameraManager.classify ? window.CameraManager.classify(e) : 'generic');
        // the manager speaks next to the pressed button (with its retry); the same
        // sentence also goes inside the viewfinder — in drive mode that is where
        // the person is looking, and the button may be off screen
        camNote(S.cam.getErrorMessage(code));
        diag('camera_failed', { code, cause: e && e.causeName });
        throw e;
    }
    S.stream = stream;
    S.streamRes = set.res;
    const v = el.video;
    v.muted = true; v.playsInline = true;
    v.srcObject = stream;
    try { await v.play(); } catch (_) {}
    await new Promise((resolve) => {
        if (v.videoWidth) return resolve();
        const done = () => { v.removeEventListener('loadedmetadata', done); v.removeEventListener('resize', done); resolve(); };
        v.addEventListener('loadedmetadata', done);
        v.addEventListener('resize', done);
        setTimeout(done, 5000);
    });
    // start() resolving is not frames arriving: a camera held by another app can
    // hand over a stream that never delivers a picture
    visibleTimeout(() => { if (S.stream === stream && !v.videoWidth) { camNote(T.cam_no_frames); diag('camera_no_frames'); } }, 6000);
    stream.getVideoTracks().forEach(t => t.addEventListener('ended', () => {
        if (S.stream !== stream) return;
        if (S.state === 'recording') stopRecording('camera_ended');
        else closeCamera();
    }));
    sizeCanvas();
    show(el.idle, false);
    capture.startPump();
    startGps();
    startMotion();
    return stream;
}

function sizeCanvas() {
    const v = el.video;
    const o = outputSize(v.videoWidth || RES_WH[set.res][0], v.videoHeight || RES_WH[set.res][1], set.res);
    if (o) capture.size(o.w, o.h);
    el.screen.classList.toggle('is-portrait', o ? o.h > o.w : false);
    const hint = $('dcPortraitHint');
    if (hint) show(hint, !!(o && o.h > o.w));
}

function closeCamera() {
    capture.stopPump();
    try { if (S.cam) S.cam.stop(); } catch (_) {}
    if (S.stream) { try { S.stream.getTracks().forEach(t => t.stop()); } catch (_) {} }
    S.stream = null;
    el.video.srcObject = null;
    // no camera, no drive: GPS is the other big battery draw
    if (S.gpsWatch != null && navigator.geolocation) { try { navigator.geolocation.clearWatch(S.gpsWatch); } catch (_) {} S.gpsWatch = null; }
    show(el.idle, true);
    if (S.state === 'preview') S.state = 'idle';
    renderState();
}

/* ─────────────────────────── storage ─────────────────────────── */

function ensureStore() {
    if (S.store) return Promise.resolve(S.store);
    if (!S.storeP) {
        S.storeP = (async () => {
            // The disk store whenever the browser has sync access handles — also in a
            // private window, whose file system lives in memory: the loop is sized by
            // the measured grant (planStorage), and a refused write shrinks it.
            const st = await openStore({ workerUrl: CFG.workerUrl });
            st.onError = (e) => onStoreError(e);
            S.store = st;
            return st;
        })();
    }
    return S.storeP;
}

/** Bytes the page may use for the loop and events when nothing measures a disk. */
function memoryBudget() {
    const dm = window.__memBudget ? window.__memBudget.gb() : 4;
    return dm >= 8 ? 400 * MB : dm >= 4 ? 220 * MB : 120 * MB;
}

/**
 * How long a loop fits. Measured, not estimated: navigator.storage.estimate()
 * answers «usage + 10 GiB» on most Chromium builds whatever the disk holds, so
 * the room is asked through the site's measuring door.
 */
async function planStorage() {
    const short = Math.min(set.res, Math.min(el.canvas.width, el.canvas.height));
    const bitrate = bitrateFor(short, 30);
    const audioBr = set.audio ? 64000 : 0;
    const perSec = (bitrate + audioBr) / 8;
    const eventReserve = Math.ceil(perSec * (set.pre + set.post) * 2 * 1.1);
    const want = loopBytes(set.loop * 60e6, bitrate, audioBr) + eventReserve;
    let free = null, measured = false;
    if (S.store.kind === 'memory') {
        free = memoryBudget();
    } else if (typeof window.__storageEstimate === 'function') {
        try {
            const est = await window.__storageEstimate({ upTo: Math.ceil(want * 1.5) });
            if (est && est.memory) free = memoryBudget();
            else if (est && est.quota) { free = Math.max(0, est.quota - est.usage); measured = !!est.measured; }
        } catch (_) { free = null; }
    }
    const choices = [1, 3, 5, 10].filter(m => m <= set.loop);
    const loopMin = free == null ? set.loop : fitLoop(choices, free, bitrate, audioBr, eventReserve);
    const need1 = loopBytes(60e6, bitrate, audioBr) + eventReserve;
    return {
        ok: loopMin > 0, loopMin, bitrate, audioBr, free, measured, want, need1,
        maxBytes: loopMin > 0 ? loopBytes(loopMin * 60e6, bitrate, audioBr) : 0,
    };
}

/**
 * The disk refused a write. For a loop segment that is not the end of the
 * drive: the loop gives up its oldest minutes and goes on, like a card that is
 * smaller than the setting — the segment is cut back to what reached the disk,
 * the next one starts on a fresh keyframe, and the byte cap drops below what is
 * held now. Only when even that leaves less than a minute does recording stop.
 * A refused write of an event file surfaces through that file's close().
 */
function onStoreError(e) {
    const file = String((e && e.file) || '');
    const quota = !!(e && (e.name === 'QuotaExceededError' || /quota|space|short write/i.test(String(e.message))));
    diag('store_write_failed', { err: errName(e), file: file.slice(0, 40), quota: quota ? 1 : 0, state: S.state });
    if (S.state !== 'recording' || !S.loop) return;
    if (!S.loop.segmentOf(e)) return;
    if (!quota) {
        stopRecording('store_error');
        return;
    }
    // cut back to what reached the disk; the next segment waits for a keyframe (asked at once)
    const { dropped } = S.loop.refused(e);
    const perMin = Math.max(1, (S.lastBitrate || 3500000) / 8 * 60);
    const cap = Math.floor(S.ix.bytes * 0.8);
    if (cap < perMin) {
        stopRecording('store_full');
        return;
    }
    S.loop.shrink(cap, Math.max(60e6, Math.floor(cap / perMin) * 60e6));
    S.sess.maxBytes = S.loop.maxBytes;
    S.sess.loopUs = S.loop.loopUs;
    toast(tr('loop_shortened_disk', { n: Math.round(S.sess.loopUs / 60e6) }), 'warning');
    ae('dashcam_disk_full_shrink', { dropped, mb: Math.round(S.ix.bytes / MB), min: Math.round(S.sess.loopUs / 60e6) });
}

async function refreshStorageLine() {
    if (!el.storage) return;
    try {
        const st = await ensureStore();
        const short = Math.min(set.res, S.stream ? Math.min(el.canvas.width, el.canvas.height) : set.res);
        const br = bitrateFor(short, 30) + (set.audio ? 64000 : 0);
        const loopB = loopBytes(set.loop * 60e6, br, 0);
        const evB = Math.ceil(br / 8 * (set.pre + set.post));
        let freeTxt;
        if (st.kind === 'memory') freeTxt = tr('storage_memory', { size: fmtBytes(memoryBudget()) });
        else {
            let est = null;
            try { est = typeof window.__storageEstimate === 'function' ? await window.__storageEstimate({ upTo: 2048 * MB }) : null; } catch (_) {}
            if (est && est.memory) freeTxt = tr('storage_memory', { size: fmtBytes(memoryBudget()) });
            else if (est && est.quota) {
                const free = Math.max(0, est.quota - est.usage);
                freeTxt = tr(free >= 2048 * MB && est.measured ? 'storage_free_atleast' : 'storage_free', { size: fmtBytes(free) });
            } else freeTxt = T.storage_unknown || '';
        }
        el.storage.textContent = freeTxt + ' ' + tr('storage_need', { loop: fmtBytes(loopB), min: set.loop, event: fmtBytes(evB) });
    } catch (_) { /* the line is advice, not a gate */ }
}

/* ─────────────────────────── sessions on disk ─────────────────────────── */

/* The session file is written twice in turn (…-0 / …-1, with a sequence
   number): a page that dies in the middle of a write leaves the other copy
   whole. Without it a torn JSON would lose the encoder configuration (the
   avcC), and with it every segment of the loop. */
function sessNames(id) { return ['sess-' + id + '-0.json', 'sess-' + id + '-1.json']; }
function segName(id, n) { return 'seg-' + id + '-' + n + '.bin'; }

function sessJson(sess, extra) {
    const c = sess.cfg || {};
    const enc = (x) => x ? Object.assign({}, x, { description: b64(x.description) }) : null;
    return JSON.stringify(Object.assign({
        v: 1, id: sess.id, wallStart: sess.wallStart, loopMin: sess.loopMin, res: sess.res,
        cfg: { video: enc(c.video), audio: enc(c.audio) },
        pending: S.pending.filter(p => p.sess === sess.id).map(p => ({ id: p.id, kind: p.kind, start: p.start, end: p.end, trig: p.trig, g: p.g, trigAt: p.trigAt, mps: p.mps, lat: p.lat, lon: p.lon })),
        clean: !!sess.clean, reason: sess.reason || null, alive: Date.now(),
    }, extra || {}));
}
/* A live recording holds a Web Lock named after its session. Another tab (or
   this page after a reload) asks for the same lock without waiting: granted
   means nobody holds it — the tab that recorded is gone (closed, killed,
   crashed) and its loop may be recovered; refused means it is still recording
   and must not be touched. The browser releases the lock with the tab, so this
   is exact where a heartbeat can only guess. */
function holdSessionLock(sess) {
    try {
        if (!navigator.locks || typeof navigator.locks.request !== 'function') return;
        navigator.locks.request('dashcam-' + sess.id, () => new Promise((res) => { sess.unlock = res; })).catch(() => {});
    } catch (_) {}
}
function releaseSessionLock(sess) { try { if (sess && sess.unlock) { sess.unlock(); sess.unlock = null; } } catch (_) {} }
/** → true when the session's tab is still alive. Without Web Locks, a heartbeat younger than 25 s says so. */
async function sessionIsLive(id, j) {
    try {
        if (navigator.locks && typeof navigator.locks.request === 'function') {
            return await navigator.locks.request('dashcam-' + id, { ifAvailable: true }, (lock) => !lock);
        }
    } catch (_) {}
    return !!(j && !j.clean && Date.now() - (j.alive || 0) < 25000);
}

function persistSession() {
    const sess = S.sess;
    if (!sess || !S.store) return Promise.resolve();
    sess.seq = (sess.seq || 0) + 1;
    return S.store.writeFile(sessNames(sess.id)[sess.seq % 2], sessJson(sess, { seq: sess.seq })).catch(() => {});
}
/** → {j, failed}: the newest whole copy; `failed` when neither could even be read (never delete then). */
async function readSession(st, id) {
    let best = null, reads = 0, errors = 0;
    for (const name of sessNames(id)) {
        let txt = null;
        try { txt = await st.readText(name); reads++; } catch (_) { errors++; continue; }
        if (!txt) continue;
        try { const j = JSON.parse(txt); if (j && j.id === id && (!best || (j.seq || 0) > (best.seq || 0))) best = j; } catch (_) { /* torn — the other copy decides */ }
    }
    return { j: best, failed: !reads && errors > 0 };
}
function cfgFromJson(j) {
    const dec = (x) => x ? Object.assign({}, x, { description: unb64(x.description) }) : null;
    return { video: dec(j.cfg && j.cfg.video), audio: dec(j.cfg && j.cfg.audio) };
}

async function readEvents() {
    try {
        const txt = await S.store.readText('events.json');
        const arr = txt ? JSON.parse(txt) : [];
        return Array.isArray(arr) ? arr : [];
    } catch (_) { return []; }
}
function persistEvents() {
    // a moment the disk refused lives in memory: it cannot come back after a reload, so it is not listed on disk
    const plain = S.events.filter(e => !e.mem).map(e => { const o = Object.assign({}, e); delete o._file; delete o._url; delete o._thumbUrl; return o; });
    return S.store.writeFile('events.json', JSON.stringify(plain)).catch((e) => diag('events_index_write', { err: errName(e) }));
}

/**
 * Loops left by earlier visits: a drive interrupted by a call, a tab the
 * system killed, a crash. Their segment files are read back by headers alone
 * (24 bytes a record) and an event that was still being recorded when the page
 * died is finished now, from what reached the disk.
 */
async function scanPrevious() {
    const st = await ensureStore();
    if (st.kind !== 'disk') return;
    let files = [];
    try { files = await st.list(); } catch (_) { return; }
    // sessions named by their own files AND by orphan segments (a session file that never got written)
    const ids = new Set();
    for (const f of files) {
        const m = /^sess-([a-z0-9]+)-[01]\.json$/.exec(f.name) || /^seg-([a-z0-9]+)-\d+\.bin$/.exec(f.name);
        if (m) ids.add(m[1]);
    }
    const out = [];
    for (const id of ids) {
        if (S.sess && S.sess.id === id) continue;
        const { j, failed } = await readSession(st, id);
        const segs = files.map(f => new RegExp('^seg-' + id + '-(\\d+)\\.bin$').exec(f.name)).filter(Boolean)
            .map(m => ({ name: m[0], n: +m[1] })).sort((a, b) => a.n - b.n);
        if (await sessionIsLive(id, j)) continue;   // another tab is recording it
        if (failed) continue;                        // unreadable right now — never a reason to delete
        if (!j || !j.cfg || !j.cfg.video || !segs.length) { await removeSession(id, segs); continue; }
        const list = [];
        for (const s of segs) {
            try {
                const h = await st.headers(s.name);
                list.push({ name: s.name, recs: headersFromWalk(h.hdr, h.offs) });
            } catch (_) { /* unreadable — skip that bucket */ }
        }
        const ix = RingIndex.fromSegments(list);
        if (!ix.span()) { await removeSession(id, segs); continue; }
        const prev = { id, j, ix, cfg: cfgFromJson(j), segs, bytes: ix.bytes, wallStart: j.wallStart, durUs: ix.durationUs() };
        out.push(prev);
        // finish events the page did not live to write
        if (Array.isArray(j.pending) && j.pending.length) {
            for (const p of j.pending) {
                const ev = Object.assign({}, p, { sess: id, end: Math.min(p.end, ix.lastVideoEnd), recovered: true });
                await saveEvent(ev, prev).catch((e) => diag('recover_event_failed', { err: errName(e) }));
            }
            j.pending = [];
            j.seq = (j.seq || 0) + 1;
            try { await st.writeFile(sessNames(id)[j.seq % 2], JSON.stringify(j)); } catch (_) {}
            ae('dashcam_recovered_event', { n: 1 });
        }
    }
    out.sort((a, b) => (b.wallStart || 0) - (a.wallStart || 0));
    S.prev = out;
    renderPrev();
    if (out.length) ae('dashcam_previous_found', { n: out.length, min: Math.round(out[0].durUs / 60e6), clean: out[0].j.clean ? 1 : 0 });
}

async function removeSession(id, segs) {
    const st = S.store;
    for (const s of segs || []) { try { await st.remove(s.name); } catch (_) {} }
    for (const n of sessNames(id)) { try { await st.remove(n); } catch (_) {} }
}

/** Before a new drive: keep the most recent previous loop, forget the older ones. */
async function trimPrevious() {
    if (S.prev.length <= 1) return;
    const old = S.prev.slice(1);
    S.prev = S.prev.slice(0, 1);
    for (const p of old) await removeSession(p.id, p.segs);
    renderPrev();
}

/* ─────────────────────────── recording ─────────────────────────── */

function newSession(plan) {
    const id = Date.now().toString(36);
    S.sess = {
        id, t0: 0, wallStart: 0, loopMin: plan.loopMin, loopUs: plan.loopMin * 60e6,
        maxBytes: plan.maxBytes, res: set.res, cfg: { video: null, audio: null }, clean: false, reason: null,
    };
    // The write side of the loop — segments on keyframes, the batch, eviction
    // under the events' locks, the cut after a refused write — is the shared
    // LoopWriter (loop-writer.js), the same one the screen recorder's instant
    // replay drives. S.ix stays the page's handle on its index.
    S.loop = new LoopWriter({
        store: S.store,
        name: (n) => segName(id, n),
        loopUs: S.sess.loopUs,
        maxBytes: S.sess.maxBytes,
        locks: lockRanges,
        requestKey: () => capture.requestKey(),
        onCreateError: (e) => onStoreError(e),
    });
    S.ix = S.loop.ix;
}

function flushBatch() {
    if (S.loop) S.loop.flush();
}

function onRecord(rec) {
    if (!S.sess || !S.loop || S.state === 'idle') return;
    // the writer evicts on a keyframe; the events' ranges are locked, so finishing
    // an event after that keeps its footage either way
    const r = S.loop.record(rec);
    if (!r || !isVideo(rec.kind)) return;
    for (const ev of S.pending.slice()) if (eventReady(ev, S.ix.lastVideoEnd)) finishEvent(ev);
}

function lockRanges() {
    const out = [];
    for (const p of S.pending) out.push([p.start, p.end]);
    for (const b of S.building) out.push([b.start, b.end]);
    return out;
}

function onConfig(kind, cfg) {
    if (!S.sess) return;
    const had = S.sess.cfg[kind];
    if (kind === 'video' && cfg.changed && had && had.description) {
        /* New parameter sets mid-recording (OpenH264 in Chromium < 130: every keyframe).
           The track keeps the FIRST configuration — the frames before the change need
           it — and capture.js puts the new sets in-band on each keyframe from now on. */
        if (!S.sess.setsChanged) { S.sess.setsChanged = true; diag('video_config_changed', { codec: cfg.codecString }); }
        return;
    }
    S.sess.cfg[kind] = cfg;
    persistSession();
    if (kind === 'video') ae('dashcam_encoder', { codec: cfg.codecString, w: cfg.width, h: cfg.height, hw: cfg.hardware ? 1 : 0 });
}

function onCaptureError(e) {
    diag('capture_error', { err: errName(e), msg: String((e && e.message) || e).slice(0, 100), state: S.state });
    if (S.state === 'recording') {
        stopRecording('encoder_error');
    }
}

async function openMic() {
    if (typeof window.MicManager !== 'function') return null;
    S.mic = S.mic || new window.MicManager({ locale: CFG.locale, autoHelp: false });
    try {
        return await S.mic.start({ echoCancellation: true, noiseSuppression: true, autoGainControl: true });
    } catch (e) {
        toast(tr('mic_failed', { reason: (e && e.message) || '' }), 'warning');
        diag('mic_failed', { err: errName(e) });
        return null;
    }
}

function askMotionPermission() {
    const DME = window.DeviceMotionEvent;
    if (DME && typeof DME.requestPermission === 'function') {
        if (S.motionPerm === 'granted') { startMotion(); return; }
        if (S.motionAsked && S.motionPerm === 'denied') return;
        S.motionAsked = true;
        try {
            DME.requestPermission().then((r) => {
                S.motionPerm = r;
                if (r === 'granted') startMotion();
                renderGState();
            }, () => { S.motionPerm = 'denied'; renderGState(); });
        } catch (_) { /* not in a gesture — the button in the settings asks again */ }
        return;
    }
    startMotion();
}

async function startRecording(btn) {
    if (S.state === 'recording' || S.state === 'stopping' || S.busy) return;
    if (!supportsRecording()) { renderUnsupported(); return; }
    S.busy = true;
    renderState();
    // Everything that needs the tap itself goes before the first await:
    // the motion permission prompt (iOS), the screen lock, full screen.
    askMotionPermission();
    try { if (window.__wakeLock) window.__wakeLock.hold('dashcam'); } catch (_) {}
    let fsP = null;
    if (set.fullscreen && isTouch() && typeof window.__safeFullscreen === 'function') fsP = window.__safeFullscreen(el.stage);
    enterDrive();
    noticeMsg('');
    try {
        await openCamera(btn);
        await ensureStore();
        await trimPrevious();
        const plan = await planStorage();
        if (!plan.ok) {
            noticeMsg(tr('err_no_room', { need: fmtBytes(plan.need1), room: fmtBytes(plan.free) }), 'error');
            diag('no_room', { free_mb: Math.round((plan.free || 0) / MB), need_mb: Math.round(plan.need1 / MB) });
            throw Object.assign(new Error('no room'), { handled: true });
        }
        if (plan.loopMin < set.loop) {
            toast(tr('loop_shortened', { n: plan.loopMin, room: fmtBytes(plan.free) }), 'warning');
            ae('dashcam_loop_shortened', { want: set.loop, got: plan.loopMin });
        }
        try { if (navigator.storage && navigator.storage.persist) navigator.storage.persist().catch(() => {}); } catch (_) {}
        const micStream = set.audio ? await openMic() : null;
        S.micStream = micStream;
        newSession(plan);
        const t0 = performance.now();
        S.sess.t0 = t0;
        S.sess.wallStart = Date.now();
        holdSessionLock(S.sess);
        await persistSession();
        S.state = 'recording';     // before the encoder starts: its first chunk must find the session open
        S.lastBitrate = plan.bitrate + plan.audioBr;
        const vinfo = await capture.startEncoding({ fps: 30, bitrate: plan.bitrate, t0 });
        let audio = null;
        if (micStream) {
            try { audio = await capture.startAudio(micStream, { workletUrl: CFG.workletUrl, t0 }); }
            catch (e) { diag('audio_start_failed', { err: errName(e) }); }
            if (!audio) toast(T.audio_unavailable, 'warning');
        }
        S.recStartedAt = Date.now();
        if (S.tripStart == null) S.tripStart = Date.now();
        startTimers();
        try { if (window.__unsavedWork) window.__unsavedWork.arm('dashcam:rec'); } catch (_) {}
        stage('recording', { res: set.res, loop: plan.loopMin, audio: audio ? 1 : 0, codec: vinfo.codec, hw: vinfo.hardware ? 1 : 0, store: S.store.kind });
        announce(T.a11y_recording);
        if (fsP) fsP.then((ok) => { if (ok) lockOrientation(); });
        S.lastTouch = Date.now();
        renderState();
        refreshLoopCard();
    } catch (e) {
        if (!e || !e.handled) {
            if (e && e.code === 'no_encoder') { renderUnsupported(); diag('no_encoder'); }
            else if (!(e && (e.code || e.name === 'CamCancelled'))) { noticeMsg(tr('err_start', { reason: errName(e) }), 'error'); diag('start_failed', { err: errName(e), msg: String((e && e.message) || '').slice(0, 100) }); }
        }
        S.state = S.stream ? 'preview' : 'idle';
        await capture.stopEncoding().catch(() => {});
        if (S.sess) releaseSessionLock(S.sess);
        if (S.sess && S.ix && !S.ix.span()) await removeSession(S.sess.id, S.ix.segs.map(x => ({ name: x.name })));
        S.sess = null; S.ix = null; S.loop = null;
        exitDrive();
        try { if (window.__wakeLock) window.__wakeLock.releaseHold('dashcam'); } catch (_) {}
    } finally {
        S.busy = false;
        renderState();
    }
}

async function stopRecording(reason) {
    if (S.state !== 'recording') return;
    S.state = 'stopping';
    S.stopReason = reason;
    renderState();
    stopTimers();
    // the pump keeps the preview alive for a user stop; everything else releases the camera
    await capture.stopEncoding().catch(() => {});
    if (S.loop) S.loop.close();
    // an event still being recorded is saved with what exists
    for (const ev of S.pending.slice()) finishEvent(ev);
    if (S.sess) {
        S.sess.clean = reason === 'user';
        S.sess.reason = reason;
        await persistSession();
        releaseSessionLock(S.sess);
    }
    persistTrip();
    try { if (window.__wakeLock) window.__wakeLock.releaseHold('dashcam'); } catch (_) {}
    try { if (window.__unsavedWork) window.__unsavedWork.disarm(); } catch (_) {}
    if (S.micStream) { try { S.micStream.getTracks().forEach(t => t.stop()); } catch (_) {} S.micStream = null; }
    try { if (S.mic) S.mic.stop(); } catch (_) {}
    setDim(false);
    exitDrive();
    const durMs = S.ix ? S.ix.durationUs() / 1000 : 0;
    const wall = S.recStartedAt ? Date.now() - S.recStartedAt : 0;
    ae('dashcam_stop', { reason, rec_s: Math.round(wall / 1000), loop_s: Math.round(durMs / 1000), events: S.events.length, drop: S.lastStats ? S.lastStats.dropped : 0 });
    // the camera goes off with the recording: a stopped dash cam should not keep the
    // phone warm (the preview button brings the picture back)
    closeCamera();
    if (reason !== 'user') {
        // the reason first (a full disk, a failed encoder), then what was kept
        const why = { store_full: T.err_disk_full, store_error: T.err_disk_write, encoder_error: T.err_encoder }[reason];
        const kept = tr(reason === 'hidden' ? 'stopped_hidden' : 'stopped_other', { kept: fmtDur(durMs) });
        noticeMsg(why ? why + ' ' + tr('stopped_kept', { kept: fmtDur(durMs) }) : kept, reason === 'hidden' ? 'warning' : 'error', reason !== 'store_full');
    } else {
        noticeMsg(tr('stopped_user', { kept: fmtDur(durMs) }), 'info');
    }
    // the finished loop becomes "the previous recording" of this page
    if (S.sess && S.ix && S.ix.span()) {
        S.prev.unshift({ id: S.sess.id, j: { clean: S.sess.clean, reason, wallStart: S.sess.wallStart }, ix: S.ix, cfg: S.sess.cfg, segs: S.ix.segs.map(s => ({ name: s.name })), bytes: S.ix.bytes, wallStart: S.sess.wallStart, durUs: S.ix.durationUs(), current: true });
    }
    await S.buildChain.catch(() => {});
    S.state = S.stream ? 'preview' : 'idle';
    renderState();
    renderPrev();
    refreshLoopCard();
}

function startTimers() {
    stopTimers();
    S.timers.batch = setInterval(flushBatch, 500);
    S.timers.hud = setInterval(renderHud, 500);
    S.timers.alive = setInterval(() => persistSession(), 10000);
    S.timers.trip = setInterval(persistTrip, 60000);
}
function stopTimers() { for (const k of Object.keys(S.timers)) { clearInterval(S.timers[k]); delete S.timers[k]; } }

/* ─────────────────────────── events ─────────────────────────── */

function snapshotThumb() {
    return new Promise((resolve) => {
        try {
            const c = el.canvas;
            const w = 320, h = Math.max(1, Math.round(320 * c.height / c.width));
            const t = document.createElement('canvas');
            t.width = w; t.height = h;
            t.getContext('2d').drawImage(c, 0, 0, w, h);
            t.toBlob((b) => resolve(b), 'image/jpeg', 0.72);
        } catch (_) { resolve(null); }
    });
}

function trigger(kind, info) {
    if (S.state !== 'recording' || !S.ix) return;
    const ts = nowUs();
    const g = info && info.g ? info.g : null;
    const last = S.pending[S.pending.length - 1];
    if (last && extendEvent(last, ts, set.post * 1e6)) {
        if (g && (!last.g || g > last.g)) last.g = g;
        if (kind === 'impact') last.kind = 'impact';
        ae('dashcam_event_extended', { kind });
        persistSession();
        renderBanner();
        return;
    }
    const span = S.ix.span();
    const ev = openEvent(ts, set.pre * 1e6, set.post * 1e6, span ? span.t0 : ts);
    Object.assign(ev, {
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 5),
        sess: S.sess.id, kind, g, trigAt: Date.now(),
        mps: S.gps && Date.now() - S.gps.wall < GPS_FRESH_MS ? S.gps.mps : null,
        lat: S.gps ? S.gps.lat : null, lon: S.gps ? S.gps.lon : null,
    });
    ev.thumbP = snapshotThumb();
    S.pending.push(ev);
    persistSession();
    renderBanner();
    flashScreen();
    announce(kind === 'impact' ? T.a11y_impact : T.a11y_saving);
    ae('dashcam_event', { kind, g: g ? Math.round(g * 100) / 100 : null, thr: set.gthr, horiz: set.ghoriz ? 1 : 0, pre: set.pre, post: set.post });
    if (kind === 'impact') setDim(false);
}

function finishEvent(ev) {
    const i = S.pending.indexOf(ev);
    if (i >= 0) S.pending.splice(i, 1);
    if (S.ix) ev.end = Math.min(ev.end, S.ix.lastVideoEnd);
    S.building.add(ev);
    flushBatch();
    S.buildChain = S.buildChain.then(() => saveEvent(ev)).catch((e) => {
        diag('event_save_failed', { err: errName(e), msg: String((e && e.message) || '').slice(0, 100) });
        toast(tr('err_event', { reason: errName(e) }), 'error');
    }).then(() => { S.building.delete(ev); persistSession(); renderBanner(); });
    renderBanner();
}

/**
 * Write one event file from a loop (the live one, or a previous one being
 * recovered): the window, the storage check, the MP4, its thumbnail, the list.
 */
async function saveEvent(ev, from) {
    const st = S.store;
    const ix = from ? from.ix : S.ix;
    const cfg = from ? from.cfg : S.sess.cfg;
    const wallStart = from ? from.wallStart : S.sess.wallStart;
    if (!cfg || !cfg.video) throw new Error('no encoder configuration');
    const win = ix.window(ev.start, ev.end);
    if (!win) throw new Error('nothing recorded in the window');
    const need = estimateBytes(win);
    // A moment is exactly what must survive a full disk: when the storage cannot
    // take the file it is kept in memory for this visit and the list says so.
    let outStore = st, inMemory = false;
    if (st.kind === 'disk' && !(await roomFor(need))) { outStore = memStore(); inMemory = true; }
    const file = 'ev-' + ev.id + '.mp4';
    let res;
    try { res = await buildMp4({ store: st, outStore, win, cfg, Muxer: window.Mp4Muxer, outName: file }); }
    catch (e) {
        try { await outStore.remove(file); } catch (_) {}      // a half-written file is not a saved moment
        if (!(e && e.name === 'QuotaExceededError') || inMemory) throw e;
        outStore = memStore(); inMemory = true;
        res = await buildMp4({ store: st, outStore, win, cfg, Muxer: window.Mp4Muxer, outName: file });
    }
    if (inMemory) { ae('dashcam_event_in_memory', { mb: Math.round(res.bytes / MB) }); toast(T.err_event_room, 'warning'); }
    let thumb = null;
    try {
        const blob = ev.thumbP ? await ev.thumbP : null;
        if (blob) { thumb = 'ev-' + ev.id + '.jpg'; await outStore.writeFile(thumb, new Uint8Array(await blob.arrayBuffer())); }
    } catch (_) { thumb = null; }
    const entry = {
        id: ev.id, file, thumb, kind: ev.kind, g: ev.g || null,
        trigAt: ev.trigAt || (wallStart + (ev.trig && ev.trig[0] || win.t0) / 1000),
        startAt: wallStart + win.t0 / 1000, durMs: Math.round(res.durationUs / 1000), bytes: res.bytes,
        mps: ev.mps == null ? null : ev.mps, lat: ev.lat == null ? null : ev.lat, lon: ev.lon == null ? null : ev.lon,
        w: cfg.video.width, h: cfg.video.height, audio: res.audioChunks > 0, recovered: !!ev.recovered,
        mem: inMemory || undefined,
    };
    S.events.unshift(entry);
    await persistEvents();
    renderEvents();
    toast(tr(ev.recovered ? 'event_recovered' : 'event_saved', { dur: fmtDur(entry.durMs) }), 'success');
    stage('event_saved', { kind: entry.kind, dur_s: Math.round(entry.durMs / 1000) });
    complete({ what: 'event', kind: entry.kind, dur_s: Math.round(entry.durMs / 1000), mb: Math.round(entry.bytes / MB), audio: entry.audio ? 1 : 0, recovered: entry.recovered ? 1 : 0 });
    return entry;
}

function storeOf(ev) { return ev.mem ? memStore() : S.store; }
async function eventFile(ev) {
    if (!ev._file) ev._file = await storeOf(ev).getFile(ev.file, 'video/mp4');
    return ev._file;
}
/** Memory for files the disk refused (moments and loop saves on a full disk). */
function memStore() { return S.mem || (S.mem = new MemoryStore()); }
/** Is there room for `bytes` more on the disk? Asked of the measuring door, never of estimate(). */
async function roomFor(bytes) {
    if (typeof window.__storageEstimate !== 'function') return true;
    try {
        const est = await window.__storageEstimate({ upTo: Math.ceil(bytes * 1.3) });
        if (!est || !est.quota || est.memory) return true;
        return est.quota - est.usage >= bytes * 1.05;
    } catch (_) { return true; }
}

function niceName(prefix, wall, ext) {
    const d = new Date(wall || Date.now());
    const p = (n) => (n < 10 ? '0' : '') + n;
    const stamp = d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + '_' + p(d.getHours()) + '-' + p(d.getMinutes()) + '-' + p(d.getSeconds());
    const on = window.__outputName;
    const base = (prefix ? prefix + '_' : '') + stamp;
    return on ? on.join(on.sanitize(base), ext) : base + '.' + ext;
}

async function downloadEvent(ev) {
    try {
        const f = await eventFile(ev);
        const name = niceName(ev.kind === 'impact' ? T.file_impact : T.file_manual, ev.trigAt, 'mp4');
        if (window.__downloadFile) window.__downloadFile(f, name);
        ae('dashcam_download', { what: 'event', mb: Math.round(ev.bytes / MB) });
        complete({ what: 'event_download', kind: ev.kind });
    } catch (e) {
        toast(tr('err_read', { reason: errName(e) }), 'error');
        diag('event_read_failed', { err: errName(e) });
    }
}

async function deleteEvent(ev) {
    let ok = true;
    if (typeof window.__confirmDialog === 'function') {
        const r = await window.__confirmDialog({ title: T.del_title, message: T.del_msg, icon: '🗑️', choices: [{ id: 'del', label: T.del_yes, primary: true }, { id: 'cancel', label: T.del_no }] });
        ok = r === 'del';
    }
    if (!ok) return;
    if (ev._url) { try { URL.revokeObjectURL(ev._url); } catch (_) {} }
    if (ev._thumbUrl) { try { URL.revokeObjectURL(ev._thumbUrl); } catch (_) {} }
    try { await storeOf(ev).remove(ev.file); } catch (_) {}
    if (ev.thumb) { try { await storeOf(ev).remove(ev.thumb); } catch (_) {} }
    S.events = S.events.filter(x => x !== ev);
    await persistEvents();
    renderEvents();
    ae('dashcam_event_deleted', {});
}

async function downloadAll() {
    const items = [];
    for (const ev of S.events) {
        if (!ev._file) continue;
        items.push({ name: niceName(ev.kind === 'impact' ? T.file_impact : T.file_manual, ev.trigAt, 'mp4'), blob: ev._file });
    }
    if (!items.length) return;
    if (!window.__batchDeliver) { for (const it of items.slice(0, 1)) window.__downloadFile(it.blob, it.name); return; }
    const r = await window.__batchDeliver.deliver(items, { zipName: niceName(T.file_events, Date.now(), 'zip'), slug: SLUG });
    ae('dashcam_download', { what: 'all', n: items.length, via: r && r.via });
}

/** The loop (live or previous) as one file. */
async function saveLoop(prev) {
    const st = await ensureStore();
    const ix = prev ? prev.ix : S.ix;
    const cfg = prev ? prev.cfg : (S.sess && S.sess.cfg);
    const wallStart = prev ? prev.wallStart : (S.sess && S.sess.wallStart);
    if (!ix || !cfg || !cfg.video) { toast(T.loop_empty, 'warning'); return; }
    if (!prev) flushBatch();
    const span = ix.span();
    const win = span && ix.window(span.t0, span.t1);
    if (!win) { toast(T.loop_empty, 'warning'); return; }
    const btn = prev ? null : el.saveLoop;
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
    const label = btn ? btn.textContent : '';
    if (btn) btn.textContent = T.loop_saving;
    try {
        if (S.lastLoopFile) { try { await st.remove(S.lastLoopFile); } catch (_) {} S.lastLoopFile = null; }
        const out = 'loop-' + Date.now().toString(36) + '.mp4';
        const progress = (f) => { if (btn) btn.textContent = T.loop_saving + ' ' + Math.round(f * 100) + '%'; };
        // on a full disk the file is put together in memory and handed over at once
        let outStore = st.kind === 'disk' && !(await roomFor(estimateBytes(win))) ? memStore() : st;
        let res;
        try {
            res = await buildMp4({ store: st, outStore, win, cfg, Muxer: window.Mp4Muxer, outName: out, onProgress: progress });
        } catch (e) {
            try { await outStore.remove(out); } catch (_) {}
            if (!(e && e.name === 'QuotaExceededError') || outStore !== st) throw e;
            outStore = memStore();
            res = await buildMp4({ store: st, outStore, win, cfg, Muxer: window.Mp4Muxer, outName: out, onProgress: progress });
        }
        const f = await outStore.getFile(out, 'video/mp4');
        if (outStore === st) S.lastLoopFile = out; else await outStore.remove(out);
        const name = niceName(T.file_loop, wallStart + win.t0 / 1000, 'mp4');
        if (window.__downloadFile) window.__downloadFile(f, name);
        ae('dashcam_download', { what: prev ? 'previous' : 'loop', s: Math.round(res.durationUs / 1e6), mb: Math.round(res.bytes / MB) });
        complete({ what: prev ? 'previous_loop' : 'loop', dur_s: Math.round(res.durationUs / 1e6) });
    } catch (e) {
        toast(tr('err_loop', { reason: errName(e) }), 'error');
        diag('loop_save_failed', { err: errName(e), msg: String((e && e.message) || '').slice(0, 100) });
    } finally {
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); btn.textContent = label; }
    }
}

/* ─────────────────────────── GPS & trip ─────────────────────────── */

function startGps() {
    if (S.gpsWatch != null || !navigator.geolocation || !Core) return;
    try {
        S.gpsWatch = navigator.geolocation.watchPosition(onPos, onPosErr, { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 });
    } catch (_) { S.gpsWatch = null; }
}
function onPos(p) {
    const c = p.coords, t = p.timestamp || Date.now();
    const pick = Core.pickSpeed(c, t, S.prevFix);
    S.prevFix = { lat: c.latitude, lon: c.longitude, accuracy: pick.accuracy, t };
    let mps = S.smoother.push(pick.mps, t);
    if (Core.isStationary(mps)) mps = 0;
    S.gps = { lat: c.latitude, lon: c.longitude, acc: c.accuracy, alt: c.altitude, mps, wall: Date.now(), source: pick.source };
    S.gpsDenied = false;
    if (S.state === 'recording' && pick.source !== 'none') {
        const fix = { lat: c.latitude, lon: c.longitude, alt: c.altitude, mps: pick.mps, accuracy: pick.accuracy, t, source: pick.source };
        if (S.trip.push(fix)) S.track.push(fix);
    } else if (S.state === 'recording' && S.track.points.length === 0) {
        // the first fix of a drive carries no speed yet — keep its position
        const fix = { lat: c.latitude, lon: c.longitude, alt: c.altitude, mps: 0, accuracy: pick.accuracy, t, source: 'derived' };
        if (S.trip.push(fix)) S.track.push(fix);
    }
    renderGps();
}
function onPosErr(e) {
    if (e && e.code === 1) { S.gpsDenied = true; ae('dashcam_gps_denied', {}); }
    renderGps();
}

function persistTrip() {
    if (!S.store || !S.trip) return;
    try {
        const j = { v: 1, start: S.tripStart, trip: S.trip.toJSON(), track: S.track.toJSON() };
        S.store.writeFile('trip.json', JSON.stringify(j)).catch(() => {});
    } catch (_) {}
}
async function restoreTrip() {
    if (!Core) return;
    try {
        const txt = await S.store.readText('trip.json');
        if (!txt) return;
        const j = JSON.parse(txt);
        S.trip = Core.TripAccumulator.fromJSON(j.trip);
        S.track = Core.TrackRecorder.fromJSON(j.track, 20000);
        S.tripStart = j.start || null;
        renderTrip();
    } catch (_) {}
}

function exportGpx() {
    const pts = S.track ? S.track.points : [];
    if (!pts.length || !window.GpsWrite) { toast(T.gpx_empty, 'warning'); return; }
    const title = tr('gpx_name', { date: fmtClock(pts[0].t) });
    const seg = { n: pts.length, lat: pts.map(p => p.lat), lon: pts.map(p => p.lon), ele: pts.map(p => (p.alt == null ? null : p.alt)), time: pts.map(p => p.t) };
    const wps = S.events.filter(e => e.lat != null && e.lon != null && (!S.tripStart || e.trigAt >= S.tripStart - 60000))
        .map(e => ({ lat: e.lat, lon: e.lon, time: e.trigAt, name: eventTitle(e), type: e.kind }));
    const xml = window.GpsWrite.gpx({ name: title, tracks: [{ name: title, segs: [seg] }], waypoints: wps }, { creator: 'Timbrica dashcam' });
    const name = niceName(T.file_track, pts[0].t, 'gpx');
    if (window.__downloadFile) window.__downloadFile(new Blob([xml], { type: 'application/gpx+xml' }), name);
    ae('dashcam_download', { what: 'gpx', pts: pts.length, wps: wps.length });
    complete({ what: 'gpx', pts: pts.length });
}

/* ─────────────────────────── motion ─────────────────────────── */

function startMotion() {
    if (S.motionOn || typeof window.DeviceMotionEvent === 'undefined') return;
    S.motionOn = true;
    window.addEventListener('devicemotion', onMotion);
    if (!S.timers.gmeter) S.timers.gmeter = setInterval(renderGMeter, 200);
    // a sensor that exists answers within a second; a desktop never does
    setTimeout(renderGState, 1500);
}
function onMotion(e) {
    const s = sampleFromEvent(e, performance.now());
    if (!s.lin && !s.incl) return;
    if (!S.motionSeen) { S.motionSeen = true; renderGState(); }
    const r = S.gs.push(s);
    S.gNow = r.g;
    if (S.lastImpact && performance.now() - S.lastImpact.t < 1500 && r.g > S.lastImpact.ev.g) S.lastImpact.ev.g = r.g;
    if (r.trigger && S.state === 'recording') {
        trigger('impact', { g: r.g });
        const ev = S.pending[S.pending.length - 1];
        if (ev) S.lastImpact = { t: performance.now(), ev };
    }
}

/* ─────────────────────────── drive mode, dim, full screen ─────────────────────────── */

function enterDrive() { el.stage.classList.add('is-live'); el.app.classList.add('is-driving'); renderState(); }
function exitDrive() {
    el.stage.classList.remove('is-live', 'is-min');
    el.app.classList.remove('is-driving');
    try {
        const fe = document.fullscreenElement || document.webkitFullscreenElement;
        if (fe === el.stage && window.__safeExitFullscreen) { window.__safeExitFullscreen(); }
    } catch (_) {}
    try { if (screen.orientation && screen.orientation.unlock) screen.orientation.unlock(); } catch (_) {}
}
function lockOrientation() {
    // lock whatever the phone is mounted at now, so a turn does not rotate the recording mid-drive
    try { if (screen.orientation && screen.orientation.lock) screen.orientation.lock(screen.orientation.type).catch(() => {}); } catch (_) {}
}
function setDim(on) {
    S.dimmed = !!on && S.state === 'recording';
    show(el.dim, S.dimmed);
    el.stage.classList.toggle('is-dim', S.dimmed);
    if (S.dimmed) ae('dashcam_dim', {});
}
function flashScreen() {
    el.screen.classList.remove('is-flash');
    void el.screen.offsetWidth;
    el.screen.classList.add('is-flash');
    setTimeout(() => el.screen.classList.remove('is-flash'), 700);
}

/* ─────────────────────────── rendering ─────────────────────────── */

function noticeMsg(msg, kind, withResume) {
    if (!el.notice) return;
    el.notice.textContent = '';
    if (!msg) { show(el.notice, false); return; }
    el.notice.dataset.kind = kind || 'info';
    const p = document.createElement('p');
    p.textContent = msg;
    el.notice.appendChild(p);
    if (withResume) {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'lg-btn lg-btn-sm dc-resume';
        b.textContent = T.btn_resume;
        b.addEventListener('click', () => { noticeMsg(''); startRecording(b); });
        el.notice.appendChild(b);
    }
    show(el.notice, true);
    // the recorder that just stopped left the full-screen stage: the reason must be in view
    // (only ever the smallest scroll that shows it — never a jump)
    if (!el.stage.classList.contains('is-live')) { try { el.notice.scrollIntoView({ block: 'nearest' }); } catch (_) {} }
}

function renderUnsupported() {
    show(el.unsupported, true);
    if (el.start) el.start.disabled = true;
}

function renderState() {
    const rec = S.state === 'recording' || S.state === 'stopping';
    show(el.idleCtl, !rec);
    show(el.liveCtl, rec);
    show(el.hud, rec);
    if (el.start) { el.start.disabled = S.busy || !supportsRecording(); el.start.setAttribute('aria-busy', S.busy ? 'true' : 'false'); }
    if (el.preview) el.preview.textContent = S.stream && !rec ? T.btn_camera_off : T.btn_preview;
    if (el.stop) el.stop.disabled = S.state === 'stopping';
    if (el.save) el.save.disabled = S.state !== 'recording';
    if (el.min) el.min.textContent = el.stage.classList.contains('is-min') ? T.btn_expand : T.btn_minimize;
    el.app.classList.toggle('is-recording', rec);
    // settings the running pipeline cannot change mid-drive
    document.querySelectorAll('#dcApp [data-lock-rec]').forEach((n) => {
        n.querySelectorAll('button, input, select').forEach((c) => { c.disabled = rec; });
        n.classList.toggle('is-locked', rec);
    });
    renderBanner();
}

function renderHud() {
    if (S.state !== 'recording') return;
    const recMs = Date.now() - S.recStartedAt;
    if (el.recTime) el.recTime.textContent = fmtDur(recMs);
    if (el.dimTime) el.dimTime.textContent = fmtDur(recMs);
    if (el.loopInfo && S.ix) el.loopInfo.textContent = tr('hud_loop', { have: fmtDur(S.ix.durationUs() / 1000), max: fmtDur(S.sess.loopUs / 1000) });
    renderBanner();
    renderGps();
    refreshLoopCard();
    renderTrip();
    if (set.autoDim && !S.dimmed && Date.now() - S.lastTouch > 30000) setDim(true);
    if (S.batt && !S.battWarned && !S.batt.charging && S.batt.level < 0.2) { S.battWarned = true; toast(T.batt_low, 'warning'); }
}

function renderBanner() {
    const ev = S.pending[S.pending.length - 1];
    const building = S.building.size > 0;
    if (!el.evBanner) return;
    if (ev && S.ix) {
        const left = Math.max(0, Math.ceil((ev.end - S.ix.lastVideoEnd) / 1e6));
        el.evBanner.dataset.kind = ev.kind;
        el.evLeft.textContent = tr(ev.kind === 'impact' ? 'banner_impact' : 'banner_manual', { s: left });
        show(el.evBanner, true);
        if (el.save) el.save.classList.add('is-pending');
    } else if (building) {
        el.evLeft.textContent = T.banner_writing;
        show(el.evBanner, true);
        if (el.save) el.save.classList.remove('is-pending');
    } else {
        show(el.evBanner, false);
        if (el.save) el.save.classList.remove('is-pending');
    }
}

function renderGps() {
    const g = S.gps, fresh = g && Date.now() - g.wall < GPS_FRESH_MS;
    if (el.speed) el.speed.textContent = fresh ? speedIn(g.mps) : '—';
    if (el.speedUnit) el.speedUnit.textContent = unitLabels[set.unit];
    if (el.gpsState) {
        el.gpsState.textContent = S.gpsDenied ? T.gps_denied : !navigator.geolocation ? T.gps_none : fresh ? tr('gps_ok', { acc: Math.round(g.acc || 0) }) : T.gps_wait;
        el.gpsState.dataset.state = S.gpsDenied ? 'off' : fresh ? 'on' : 'wait';
    }
}

function renderGMeter() {
    const g = S.gNow || 0, peak = S.gs.peak();
    const max = 3.5;
    if (el.gBar) el.gBar.style.inlineSize = Math.min(100, g / max * 100) + '%';
    if (el.gMark) el.gMark.style.insetInlineStart = Math.min(100, set.gthr / max * 100) + '%';
    if (el.gVal) el.gVal.textContent = g.toFixed(2) + ' g';
    if (el.gPeak) el.gPeak.textContent = tr('g_peak', { g: peak.toFixed(2) });
    if (el.gNow) el.gNow.textContent = g.toFixed(1) + ' g';
    if (el.gMeter) el.gMeter.classList.toggle('is-over', g >= set.gthr);
}

function renderGState() {
    if (!el.gState) return;
    const DME = window.DeviceMotionEvent;
    const needsPerm = DME && typeof DME.requestPermission === 'function' && S.motionPerm !== 'granted';
    show(el.gPerm, !!needsPerm);
    let txt;
    if (typeof DME === 'undefined') txt = T.g_none;
    else if (needsPerm && S.motionPerm === 'denied') txt = T.g_denied;
    else if (needsPerm) txt = T.g_perm;
    else if (S.motionSeen) txt = S.gs.haveLinear ? T.g_ok : T.g_ok_raw;
    else if (S.motionOn) txt = T.g_none;
    else txt = T.g_wait;
    el.gState.textContent = txt;
    el.gState.dataset.state = S.motionSeen ? 'on' : 'off';
}

function refreshLoopCard() {
    const ix = S.ix, has = ix && ix.span() && S.sess && S.sess.cfg && S.sess.cfg.video;
    show(el.loopCard, !!has);
    if (!has) return;
    const recording = S.state === 'recording';
    el.loopStatus.textContent = tr(recording ? 'loop_status_live' : 'loop_status_done', {
        have: fmtDur(ix.durationUs() / 1000), max: fmtDur(S.sess.loopUs / 1000), size: fmtBytes(ix.bytes),
    });
}

function renderTrip() {
    if (!Core || !S.trip) return;
    const u = set.unit;
    const d = Core.formatDistance(S.trip.distance, u);
    if (el.trip.dist) el.trip.dist.textContent = d.value + ' ' + (T['u_' + d.unit] || d.unit);
    if (el.trip.time) el.trip.time.textContent = fmtDur(S.trip.movingMs);
    if (el.trip.max) el.trip.max.textContent = speedIn(S.trip.maxMps) + ' ' + unitLabels[u];
    if (el.trip.avg) el.trip.avg.textContent = speedIn(S.trip.avgMps()) + ' ' + unitLabels[u];
    if (el.gpx) el.gpx.disabled = !(S.track && S.track.points.length);
}

function eventTitle(e) {
    return e.kind === 'impact' ? (e.g ? tr('ev_impact_g', { g: e.g.toFixed(1) }) : T.ev_impact) : T.ev_manual;
}

function renderEvents() {
    const list = el.evList;
    if (!list) return;
    list.textContent = '';
    show(el.evEmpty, !S.events.length);
    show(el.evAll, S.events.length > 1);
    const total = S.events.reduce((s, e) => s + (e.bytes || 0), 0);
    if (el.evTotal) el.evTotal.textContent = S.events.length ? tr('ev_total', { n: S.events.length, size: fmtBytes(total) }) : '';
    for (const e of S.events) {
        const li = document.createElement('li');
        li.className = 'dc-ev';
        li.dataset.kind = e.kind;
        const fig = document.createElement('div');
        fig.className = 'dc-ev-thumb';
        if (e.thumb) {
            const img = document.createElement('img');
            img.alt = '';
            img.width = 160; img.height = Math.round(160 * (e.h || 9) / (e.w || 16));
            fig.appendChild(img);
            (async () => {
                try {
                    if (!e._thumbUrl) e._thumbUrl = URL.createObjectURL(await storeOf(e).getFile(e.thumb, 'image/jpeg'));
                    img.src = e._thumbUrl;
                } catch (_) { fig.classList.add('is-empty'); }
            })();
        } else fig.classList.add('is-empty');
        li.appendChild(fig);
        const body = document.createElement('div');
        body.className = 'dc-ev-body';
        const h = document.createElement('div');
        h.className = 'dc-ev-title';
        h.textContent = (e.kind === 'impact' ? '💥 ' : '📌 ') + eventTitle(e);
        body.appendChild(h);
        const meta = document.createElement('div');
        meta.className = 'dc-ev-meta';
        const bits = [fmtClock(e.trigAt), fmtDur(e.durMs), fmtBytes(e.bytes)];
        if (e.mps != null) bits.push(speedIn(e.mps) + ' ' + unitLabels[set.unit]);
        if (e.recovered) bits.push(T.ev_recovered);
        if (e.mem) bits.push('⚠️ ' + T.ev_in_memory);
        meta.textContent = bits.join(' · ');
        body.appendChild(meta);
        if (e.lat != null && e.lon != null) {
            const a = document.createElement('a');
            a.className = 'dc-ev-map';
            a.href = 'https://www.openstreetmap.org/?mlat=' + e.lat.toFixed(6) + '&mlon=' + e.lon.toFixed(6) + '#map=17/' + e.lat.toFixed(6) + '/' + e.lon.toFixed(6);
            a.target = '_blank'; a.rel = 'noopener nofollow';
            a.textContent = '📍 ' + e.lat.toFixed(5) + ', ' + e.lon.toFixed(5);
            body.appendChild(a);
        }
        const acts = document.createElement('div');
        acts.className = 'dc-ev-acts';
        const bPlay = mkBtn('▶ ' + T.ev_play, 'dc-ev-play');
        const bDown = mkBtn('⤓ ' + T.ev_download, 'dc-ev-dl', true);
        const bDel = mkBtn('🗑️ ' + T.ev_delete, 'dc-ev-del');
        acts.append(bPlay, bDown, bDel);
        body.appendChild(acts);
        const player = document.createElement('div');
        player.className = 'dc-ev-player';
        player.hidden = true;
        body.appendChild(player);
        li.appendChild(body);
        bPlay.addEventListener('click', async () => {
            if (!player.hidden) { player.hidden = true; player.textContent = ''; bPlay.textContent = '▶ ' + T.ev_play; return; }
            try {
                const f = await eventFile(e);
                if (!e._url) e._url = URL.createObjectURL(f);
                const v = document.createElement('video');
                v.controls = true; v.playsInline = true; v.preload = 'metadata'; v.src = e._url;
                player.appendChild(v);
                player.hidden = false;
                bPlay.textContent = '■ ' + T.ev_close;
                v.play().catch(() => {});
                ae('dashcam_play', {});
            } catch (err) { toast(tr('err_read', { reason: errName(err) }), 'error'); }
        });
        bDown.addEventListener('click', () => downloadEvent(e));
        bDel.addEventListener('click', () => deleteEvent(e));
        // the File handle is fetched ahead, so «save all» can call the folder picker
        // as the first thing in its click (the picker needs the tap's activation)
        eventFile(e).catch(() => {});
        list.appendChild(li);
    }
}

function mkBtn(text, cls, primary) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'lg-btn lg-btn-sm dc-btn-sm ' + (primary ? '' : 'lg-btn-ghost ') + (cls || '');
    b.textContent = text;
    return b;
}

function renderPrev() {
    const list = el.prevList;
    if (!list) return;
    list.textContent = '';
    // the loop of THIS page's drive has its own card; this one lists what came before it
    const items = S.prev.filter(p => p.ix !== S.ix);
    show(el.prevCard, items.length > 0);
    for (const p of items) {
        const li = document.createElement('li');
        li.className = 'dc-prev';
        const txt = document.createElement('div');
        txt.className = 'dc-prev-txt';
        const why = p.j && !p.j.clean ? (p.j.reason === 'hidden' ? T.prev_hidden : T.prev_crash) : T.prev_stopped;
        txt.textContent = tr('prev_line', { when: fmtClock(p.wallStart || Date.now()), dur: fmtDur(p.durUs / 1000), size: fmtBytes(p.bytes) }) + ' — ' + why;
        const acts = document.createElement('div');
        acts.className = 'dc-ev-acts';
        const bSave = mkBtn('⤓ ' + T.prev_save, 'dc-prev-save', true);
        const bDel = mkBtn('🗑️ ' + T.prev_delete, 'dc-prev-del');
        bSave.addEventListener('click', async () => {
            bSave.disabled = true;
            try { await saveLoop(p); } finally { bSave.disabled = false; }
        });
        bDel.addEventListener('click', async () => {
            await removeSession(p.id, p.segs);
            S.prev = S.prev.filter(x => x !== p);
            renderPrev();
            refreshStorageLine();
        });
        acts.append(bSave, bDel);
        li.append(txt, acts);
        list.appendChild(li);
    }
}

/* ─────────────────────────── settings UI ─────────────────────────── */

function bindSeg(key, num) {
    document.querySelectorAll('[data-seg="' + key + '"]').forEach((group) => {
        const paint = () => group.querySelectorAll('button[data-v]').forEach((b) => {
            const on = String(set[key]) === b.dataset.v;
            b.setAttribute('aria-checked', on ? 'true' : 'false');
            b.tabIndex = on ? 0 : -1;
        });
        group.addEventListener('click', (e) => {
            const b = e.target.closest('button[data-v]');
            if (!b || b.disabled) return;
            save(key, num ? Number(b.dataset.v) : b.dataset.v);
            paint();
            onSettingChanged(key);
        });
        group.addEventListener('keydown', (e) => {
            if (!/^Arrow(Left|Right|Up|Down)$/.test(e.key)) return;
            const bs = [...group.querySelectorAll('button[data-v]')].filter(b => !b.disabled);
            const i = bs.findIndex(b => b.getAttribute('aria-checked') === 'true');
            const rtl = document.documentElement.dir === 'rtl';
            const fwd = e.key === 'ArrowDown' || (e.key === (rtl ? 'ArrowLeft' : 'ArrowRight'));
            const n = bs[(i + (fwd ? 1 : -1) + bs.length) % bs.length];
            if (n) { e.preventDefault(); n.click(); n.focus(); }
        });
        paint();
    });
}

function bindCheck(id, key) {
    const n = $(id);
    if (!n) return;
    n.checked = !!set[key];
    n.addEventListener('change', () => { save(key, n.checked); onSettingChanged(key); });
}

function onSettingChanged(key) {
    if (key === 'res' && S.stream && S.state === 'preview') openCamera(null).catch(() => {});
    if (key === 'unit') { renderGps(); renderTrip(); renderEvents(); }
    if (key === 'ghoriz') S.gs.setHorizontal(set.ghoriz);
    if (key === 'loop' && set.pre > set.loop * 60) { save('pre', Math.min(60, set.loop * 60)); bindSegPaint('pre'); }
    if (key === 'pre' && set.pre > set.loop * 60) { save('loop', 3); bindSegPaint('loop'); }
    if (['res', 'loop', 'audio', 'pre', 'post'].includes(key)) refreshStorageLine();
    ae('dashcam_setting', { k: key, v: typeof set[key] === 'string' && key === 'text' ? (set.text ? 1 : 0) : set[key] });
}
function bindSegPaint(key) {
    document.querySelectorAll('[data-seg="' + key + '"] button[data-v]').forEach((b) => {
        b.setAttribute('aria-checked', String(set[key]) === b.dataset.v ? 'true' : 'false');
    });
}

function wireSettings() {
    bindSeg('res', true); bindSeg('loop', true); bindSeg('pre', true); bindSeg('post', true); bindSeg('unit', false);
    bindCheck('dcAudio', 'audio'); bindCheck('dcGHoriz', 'ghoriz');
    bindCheck('dcOvTime', 'ovTime'); bindCheck('dcOvSpeed', 'ovSpeed'); bindCheck('dcOvCoords', 'ovCoords'); bindCheck('dcOvText', 'ovText');
    bindCheck('dcAutoDim', 'autoDim'); bindCheck('dcFullscreen', 'fullscreen');
    if (el.gThr) {
        el.gThr.value = String(set.gthr);
        const upd = () => {
            const v = Math.round(Number(el.gThr.value) * 10) / 10;
            if (el.gThrVal) el.gThrVal.textContent = v.toFixed(1) + ' g';
            return v;
        };
        upd();
        el.gThr.addEventListener('input', () => { const v = upd(); set.gthr = v; S.gs.setThreshold(v); renderGMeter(); });
        el.gThr.addEventListener('change', () => { save('gthr', upd()); ae('dashcam_setting', { k: 'gthr', v: set.gthr }); });
        document.querySelectorAll('[data-gpreset]').forEach((b) => b.addEventListener('click', () => {
            const v = Number(b.dataset.gpreset);
            el.gThr.value = String(v); save('gthr', v); upd(); S.gs.setThreshold(v); renderGMeter();
        }));
    }
    if (el.textIn) {
        el.textIn.value = set.text || '';
        el.textIn.addEventListener('input', () => {
            save('text', el.textIn.value.slice(0, 24));
            const box = $('dcOvText');
            if (box && el.textIn.value && !box.checked) { box.checked = true; save('ovText', true); }
        });
    }
    if (el.gPerm) el.gPerm.addEventListener('click', () => { S.motionAsked = false; S.motionPerm = null; askMotionPermission(); });
}

/* ─────────────────────────── wiring ─────────────────────────── */

function wire() {
    el.start.addEventListener('click', () => startRecording(el.start));
    el.preview.addEventListener('click', async () => {
        if (S.stream && S.state !== 'recording') { closeCamera(); return; }
        askMotionPermission();
        try { await openCamera(el.preview); S.state = 'preview'; stage('preview'); } catch (_) {}
        renderState();
        refreshStorageLine();
    });
    el.stop.addEventListener('click', () => stopRecording('user'));
    el.save.addEventListener('click', () => trigger('manual', null));
    el.dimBtn.addEventListener('click', () => setDim(true));
    el.dim.addEventListener('click', () => { setDim(false); S.lastTouch = Date.now(); });
    el.fs.addEventListener('click', () => {
        const fe = document.fullscreenElement || document.webkitFullscreenElement;
        if (fe) { if (window.__safeExitFullscreen) window.__safeExitFullscreen(); return; }
        if (typeof window.__safeFullscreen === 'function') window.__safeFullscreen(el.stage).then((ok) => { if (ok) lockOrientation(); });
    });
    el.min.addEventListener('click', () => {
        const min = !el.stage.classList.contains('is-min');
        el.stage.classList.toggle('is-min', min);
        el.stage.classList.toggle('is-live', !min);
        if (min) {
            try { const fe = document.fullscreenElement || document.webkitFullscreenElement; if (fe && window.__safeExitFullscreen) window.__safeExitFullscreen(); } catch (_) {}
            el.stage.scrollIntoView({ block: 'nearest' });
        }
        renderState();
    });
    el.stage.addEventListener('pointerdown', () => { S.lastTouch = Date.now(); }, { passive: true });
    el.saveLoop.addEventListener('click', () => saveLoop(null));
    el.evAll.addEventListener('click', () => downloadAll());
    el.gpx.addEventListener('click', exportGpx);
    el.tripReset.addEventListener('click', () => {
        S.trip.reset(); S.track.reset(); S.tripStart = S.state === 'recording' ? Date.now() : null;
        persistTrip(); renderTrip();
        ae('dashcam_trip_reset', {});
    });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'hidden') return;
        if (S.state === 'recording') stopRecording('hidden');
        else if (S.state === 'preview' || S.stream) closeCamera();
    });
    window.addEventListener('pagehide', () => {
        // best effort: what is batched reaches the worker; the worker flushes on its own clock
        try { flushBatch(); if (S.store && S.store.flush) S.store.flush().catch(() => {}); } catch (_) {}
        if (S.sess && S.state === 'recording') persistSession();
    });
    document.addEventListener('fullscreenchange', () => {
        const fe = document.fullscreenElement;
        if (el.fs) el.fs.textContent = fe === el.stage ? T.btn_fs_exit : T.btn_fs;
    });
    // the battery through the shared reader: a browser that hides the battery
    // answers «100 %, charging», which must not read as a real level
    const BS = window.__batteryStatus;
    if (BS && typeof BS.read === 'function') {
        const upd = (r) => {
            S.batt = r && r.state === 'ok' ? r : null;
            if (!el.batt) return;
            if (!r || r.state !== 'ok') { show(el.batt, false); return; }
            el.batt.textContent = (r.charging ? '⚡' : '🔋') + ' ' + r.percent + '%';
            el.batt.dataset.low = !r.charging && r.level < 0.2 ? '1' : '0';
            show(el.batt, true);
        };
        BS.onChange(upd);
        BS.read().then(upd, () => {});
    }
}

/* ─────────────────────────── boot ─────────────────────────── */

async function boot() {
    if (!el.app) return;
    wireSettings();
    wire();
    renderState();
    renderGMeter();
    renderGState();
    renderGps();
    if (!supportsRecording()) renderUnsupported();
    const st = await ensureStore();
    if (st.kind === 'memory') { const n = $('dcMemNote'); show(n, true); }
    S.events = await readEvents();
    // an entry whose file is gone (site data cleared, a failed write) is not an event
    if (st.kind === 'disk' && S.events.length) {
        let names = new Set();
        try { names = new Set((await st.list()).map(f => f.name)); } catch (_) { names = null; }
        if (names) {
            const before = S.events.length;
            S.events = S.events.filter(e => names.has(e.file));
            if (S.events.length !== before) persistEvents();
        }
    }
    renderEvents();
    await restoreTrip();
    renderTrip();
    refreshStorageLine();
    scanPrevious().catch((e) => diag('scan_previous_failed', { err: errName(e) }));
    stage('ready', { store: st.kind, wc: supportsRecording() ? 1 : 0 });
}

// A seam for the page's own end-to-end test (tests/playwright/dashcam-e2e.mjs):
// read-only views of the state plus the one call a test cannot make by hand.
window.__dashcamTest = {
    state: () => S.state,
    layout: () => capture.layout,
    canvasSize: () => ({ w: el.canvas.width, h: el.canvas.height }),
    events: () => S.events.map(e => ({ id: e.id, kind: e.kind, durMs: e.durMs, bytes: e.bytes, g: e.g, file: e.file, lat: e.lat, lon: e.lon, mps: e.mps, recovered: e.recovered, mem: !!e.mem })),
    pending: () => S.pending.length + S.building.size,
    loop: () => (S.ix ? { us: S.ix.durationUs(), bytes: S.ix.bytes, segs: S.ix.segs.length } : null),
    prev: () => S.prev.map(p => ({ id: p.id, durUs: p.durUs, bytes: p.bytes, clean: !!(p.j && p.j.clean) })),
    storeKind: () => (S.store ? S.store.kind : null),
    gNow: () => S.gNow,
    stats: () => S.lastStats || null,
    eventBytes: async (id) => {
        const e = S.events.find(x => x.id === id);
        if (!e) return null;
        const f = await storeOf(e).getFile(e.file, 'video/mp4');
        return new Uint8Array(await f.arrayBuffer());
    },
    eventUrl: async (id) => {
        const e = S.events.find(x => x.id === id);
        if (!e) return null;
        return URL.createObjectURL(await eventFile(e));
    },
    trip: () => (S.trip ? { d: S.trip.distance, max: S.trip.maxMps, pts: S.track.points.length } : null),
};

boot().catch((e) => { diag('boot_failed', { err: errName(e), msg: String((e && e.message) || '').slice(0, 100) }); });
